var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Box from '@mui/material/Box';
import { useMemo, useState, useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import { Link, useSearchParams } from 'react-router-dom';
import { PRODUCT_CLASS_ALLOWED, CPA_SCOPES, UI_WRITE_SCOPES } from 'utils/constants';
import { useCheckAuthScope } from 'services/helperFunctions/useCheckAuthScope';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { CpaQuickView } from 'components/Shared/CpaFolder/CpaQuickView';
import { MouseHoverPopoverModal } from '../../MouseHoverPopoverModal/MouseHoverPopoverModal';
import styles from './PubTableActionCell.module.scss';
export function PubTableActionCell(_a) {
    var icon = _a.icon, _b = _a.disabled, disabled = _b === void 0 ? false : _b, _c = _a.isAddRow, isAddRow = _c === void 0 ? false : _c, extraIconIsAction = _a.extraIconIsAction, tableData = _a.tableData, indexOfItem = _a.indexOfItem, isHovering = _a.isHovering, def = _a.def, origin = _a.origin, handleActionClick = _a.handleActionClick;
    var sParams = useSearchParams()[0];
    var _d = useState(null), quickViewAnchorEl = _d[0], setQuickViewAnchorEl = _d[1];
    var QUICK_VIEW_LABEL = 'Quick View';
    var userHasCPAPrivileges = useCheckAuthScope(CPA_SCOPES);
    var userHasEditPrivileges = useCheckAuthScope(UI_WRITE_SCOPES);
    var currentRow = tableData && indexOfItem !== undefined ? tableData[indexOfItem] : null;
    var isCpaOrWorkOrder = useCallback(function () { return currentRow && currentRow.productClass && PRODUCT_CLASS_ALLOWED.includes(currentRow.productClass); }, [currentRow]);
    var _e = useState(false), showQuickViewModal = _e[0], setShowQuickViewModal = _e[1];
    var showQuickView = useMemo(function () {
        var hasDeviations = currentRow && currentRow.deviations;
        return hasDeviations && isCpaOrWorkOrder();
    }, [currentRow, isCpaOrWorkOrder]);
    // Limit deviation description to 200 characters
    var truncatedDeviationDesc = useMemo(function () {
        if (!(currentRow === null || currentRow === void 0 ? void 0 : currentRow.deviations))
            return '';
        return currentRow.deviations.length > 200
            ? "".concat(currentRow.deviations.substring(0, 200), "...")
            : currentRow.deviations;
    }, [currentRow === null || currentRow === void 0 ? void 0 : currentRow.deviations]);
    var handleQuickViewMouseEnter = function (event) {
        setQuickViewAnchorEl(event.currentTarget);
    };
    var handleQuickViewMouseLeave = function () {
        setQuickViewAnchorEl(null);
    };
    var labels = useMemo(function () {
        var _a, _b;
        var arr = [];
        if (showQuickView) {
            arr.push(QUICK_VIEW_LABEL);
        }
        if (tableData && ((_b = (_a = tableData[indexOfItem]) === null || _a === void 0 ? void 0 : _a.version) === null || _b === void 0 ? void 0 : _b.includes('KNOLL')) && userHasEditPrivileges) {
            arr.push('Eng Info');
        }
        // Add CPA link for CPA users, otherwise add Build link
        if (process.env.REACT_APP_ENABLE_CPA === 'TRUE' && userHasCPAPrivileges && isCpaOrWorkOrder()) {
            arr.push('CPA');
        }
        else {
            arr.push('Build');
        }
        arr.push('Info');
        return arr;
    }, [indexOfItem, tableData, showQuickView, isCpaOrWorkOrder, userHasCPAPrivileges, userHasEditPrivileges]);
    var buildRoute = function (label) {
        var _a;
        if (label === QUICK_VIEW_LABEL) {
            return '';
        }
        var startDate = tableData[indexOfItem].startDate;
        var _b = startDate.split(' @ '), startDatePart = _b[0], startTimePart = _b[1];
        var isSimpleOrigin = origin === 'simple';
        var userDate = sParams.get('date');
        var userTime = sParams.get('time');
        var formattedDate = isSimpleOrigin && userDate ? userDate : startDatePart.replace(/\//g, '-');
        var formattedTime = isSimpleOrigin && userTime ? userTime : "".concat(startTimePart, ":59");
        var routeString = '';
        switch (label) {
            case 'CPA':
                routeString = "/cpa/product/".concat(((_a = tableData[indexOfItem]) === null || _a === void 0 ? void 0 : _a.prefix) || '');
                break;
            case 'Build':
                routeString = "/product-build/build?productNbr=".concat(tableData[indexOfItem].prefix, "&date=").concat(formattedDate, "&time=").concat(formattedTime, "&channel=Commercial&pid=").concat(tableData[indexOfItem].id, "&productVersion=").concat(tableData[indexOfItem].version, "&origin=").concat(origin);
                break;
            case 'Info':
                routeString = "/product-info/info?prefix=".concat(tableData[indexOfItem].prefix, "&date=").concat(formattedDate, "&time=").concat(formattedTime, "&channel=Commercial&pid=").concat(tableData[indexOfItem].id, "&productVersion=").concat(tableData[indexOfItem].version, "&origin=").concat(origin);
                break;
            case 'Eng Info':
                routeString = "/engdatamaint/product/".concat(tableData[indexOfItem].id);
                break;
            default:
                break;
        }
        return routeString;
    };
    var handleLinkClick = function () {
        sessionStorage.setItem('infoPageOrigin', origin);
    };
    return (_jsx(TableCell, __assign({ sx: {
            paddingX: '4px',
            backgroundColor: isAddRow ? 'white' : 'inherit',
            position: 'relative',
        } }, { children: handleActionClick !== undefined && !extraIconIsAction ? (_jsx(IconButton, __assign({ disabled: disabled, onClick: function () { return handleActionClick(); }, sx: {
                position: 'float',
                left: isHovering ? '15px' : '0',
            } }, { children: icon }))) : (_jsx(Box, __assign({ className: styles.actionLinksContainer }, { children: labels &&
                labels.length > 0 &&
                labels.map(function (label) { return (_jsx(Box, { children: label === QUICK_VIEW_LABEL ? (_jsxs(_Fragment, { children: [_jsxs(Box, __assign({ className: styles.quickViewContainer }, { children: [_jsxs(Box, __assign({ onMouseEnter: handleQuickViewMouseEnter, onMouseLeave: handleQuickViewMouseLeave, className: styles.quickViewLink }, { children: [_jsx(FormatListBulletedIcon, { className: styles.quickViewIcon }), _jsx(Typography, __assign({ variant: "body2", component: "span", className: styles.quickViewLabel }, { children: QUICK_VIEW_LABEL }))] })), _jsx(Box, { className: styles.verticalDivider })] })), _jsx(MouseHoverPopoverModal, { open: Boolean(quickViewAnchorEl), anchorEl: quickViewAnchorEl, onClose: handleQuickViewMouseLeave, onMouseEnter: handleQuickViewMouseEnter, onMouseLeave: handleQuickViewMouseLeave, header: currentRow === null || currentRow === void 0 ? void 0 : currentRow.name, description: truncatedDeviationDesc, onOpenPopover: function () {
                                    setShowQuickViewModal(true);
                                } }), showQuickViewModal && userHasCPAPrivileges && (_jsx(CpaQuickView, { isOpen: true, closeModal: function () { return setShowQuickViewModal(false); }, cpn: currentRow.prefix, standardProductRef: currentRow.standardProductRef, productVersion: tableData[indexOfItem].version, productId: tableData[indexOfItem].id }))] })) : (_jsx(Box, __assign({ className: "".concat(styles.actionLinkBox, " ").concat(label === 'Eng Info' ? styles.engInfoWidth : styles.normalWidth) }, { children: _jsx(Link, __assign({ className: styles.link, to: buildRoute(label), onClick: function () { return handleLinkClick(); }, target: "_blank" }, { children: label })) }))) }, label)); }) }))) })));
}
